import { map } from 'lodash';
import { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { TextKey } from '@mews-commander/enums';
import { TimeUnit } from '@mews/enums';
import { DateTimeService, LocalizedText } from '@mews/framework';
import { createHandler } from '@mews/std';
import { getTextTokenPreset, getToken, withBreakpointRange } from '@optimus-web/core';
import {
    asyncContainerFactory,
    LocalizedMonthFromUtc,
    LocalizedDayNameFromUtc,
    LocalizedShortDayNameFromUtc,
    LocalizedYearFromUtc,
    LocalizedShortDateFromUtc,
    LocalizedShortMonthFromUtc,
    LocalizedShortTimeFromUtc,
    WithBreakpointRange,
} from '@mews-commander/ui';
import { getSelectedServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/serviceSelect';
import { OccupancyChart } from '../../charts';
import { getMaxOccupancyAxisValue, getOccupancyData, getOccupancyTableData, isFetching, hasFailed } from '../selectors';
import {
    COLOR_ARRIVALS,
    COLOR_DEPARTURES,
    NUMBER_AXIS_WIDTH,
    PERCENT_AXIS_WIDTH,
} from '../../charts/OccupancyChart/constants';
import OccupancyReportWidgetHeader from './OccupancyReportWidgetHeader';
import OccupancyReportDate from './OccupancyReportDate';

const getDateConfiguration = createHandler({
    [TimeUnit.Month]: {
        PrimaryTitleComponent: LocalizedMonthFromUtc,
        ShortPrimaryTitleComponent: LocalizedShortMonthFromUtc,
        SecondaryTitleComponent: LocalizedYearFromUtc,
    },
    [TimeUnit.Day]: {
        PrimaryTitleComponent: LocalizedDayNameFromUtc,
        ShortPrimaryTitleComponent: LocalizedShortDayNameFromUtc,
        SecondaryTitleComponent: LocalizedShortDateFromUtc,
    },
    [TimeUnit.Hour]: {
        PrimaryTitleComponent: LocalizedShortTimeFromUtc,
        ShortPrimaryTitleComponent: LocalizedShortTimeFromUtc,
        SecondaryTitleComponent: LocalizedShortDateFromUtc,
    },
});

interface Props extends WithBreakpointRange {
    chartData: {}[];
    tableData: {};
    maxYValue: number;
    onUnmount?: $TSFixMeFunction;
    renderButtons?: boolean;
}

class OccupancyReportWidgetComponent extends Component<Props> {
    componentWillUnmount() {
        const { onUnmount } = this.props;
        if (onUnmount) {
            onUnmount();
        }
    }

    renderDate = (date: $TSFixMe, index: $TSFixMe) => {
        // @ts-expect-error $TSFixMeError: Property 'timeUnit' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
        const { breakpointRange, timeUnit } = this.props;
        const utcDate = DateTimeService.toUtc(date).value;
        const configuration = getDateConfiguration(timeUnit);
        // @ts-expect-error $TSFixMeError: Argument of type '{ PrimaryTitleComponent: ConnectedComponent<({ utcString, timezone }: LocalizedUtcComponentProps) => E...
        const DateComponent = OccupancyReportDate(configuration);

        return (
            // @ts-expect-error $TSFixMeError: Type '{ key: string; breakpointRange: any; utcDate: string; }' is missing the following properties from type 'OccupancyR...
            <DateComponent key={`occupancyTableDate-${index}`} breakpointRange={breakpointRange} utcDate={utcDate} />
        );
    };

    render() {
        const { tableData, chartData, maxYValue, renderButtons } = this.props;
        return (
            <ContentElement>
                {renderButtons && <OccupancyReportWidgetHeader />}
                <OccupancyChart data={chartData} maxYValue={maxYValue} />
                <TableWrapperElement>
                    <TableElement width="100%">
                        <tbody>
                            <DateRowElement>
                                <TableHeaderElement>
                                    <LocalizedText textKey={TextKey.Date} />
                                </TableHeaderElement>
                                {map((tableData as $TSFixMe).timeUnits, this.renderDate)}
                            </DateRowElement>
                            <ArrivalsRowElement>
                                <TableHeaderElement>
                                    <LocalizedText textKey={TextKey.Arrivals} />
                                </TableHeaderElement>
                                {map((tableData as $TSFixMe).arrivals, (item, index) => (
                                    <ArrivalsCellElement key={index}>{item}</ArrivalsCellElement>
                                ))}
                            </ArrivalsRowElement>
                            <tr>
                                <TableHeaderElement>
                                    <LocalizedText textKey={TextKey.Departures} />
                                </TableHeaderElement>
                                {map((tableData as $TSFixMe).departures, (item, index) => (
                                    <DeparturesCellElement key={index}>{item}</DeparturesCellElement>
                                ))}
                            </tr>
                            <tr>
                                <TableHeaderElement>
                                    <LocalizedText textKey={TextKey.StayOvers} />
                                </TableHeaderElement>
                                {map((tableData as $TSFixMe).stayOvers, (item, index) => (
                                    <TableCellElement key={index}>{item}</TableCellElement>
                                ))}
                            </tr>
                            <tr>
                                <TableHeaderElement>
                                    <LocalizedText textKey={TextKey.CustomerPlural} />
                                </TableHeaderElement>
                                {map((tableData as $TSFixMe).customers, (item, index) => (
                                    <TableCellElement key={index}>{item}</TableCellElement>
                                ))}
                            </tr>
                        </tbody>
                    </TableElement>
                </TableWrapperElement>
            </ContentElement>
        );
    }
}

const connectStore = connect(
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    createStructuredSelector({
        chartData: getOccupancyData,
        tableData: getOccupancyTableData,
        maxYValue: getMaxOccupancyAxisValue,
        isFetching,
        hasFailed,
        timeUnit: getSelectedServiceTimeUnit,
    })
);

export default compose(
    withBreakpointRange,
    connectStore,
    asyncContainerFactory({ withOverlayedLoader: true })
)(OccupancyReportWidgetComponent);

const TableWrapperElement = styled.div`
    padding-right: ${NUMBER_AXIS_WIDTH}px;
    margin-top: 16px;
`;

const TableElement = styled.table`
    width: 100%;
    table-layout: fixed;
`;

const TableHeaderElement = styled.th`
    width: ${PERCENT_AXIS_WIDTH}px;
    ${getTextTokenPreset('bodyMediumStrong')};
`;

const DateRowElement = styled.tr`
    border-bottom: 1px solid ${getToken('borderStaticPrimary')};

    td,
    th {
        padding-bottom: 12px;
    }
`;

const ArrivalsRowElement = styled.tr`
    td,
    th {
        padding-top: 12px;
    }
`;

const TableCellElement = styled.td`
    text-align: center;
`;

const ChartDataCellElement = styled(TableCellElement)`
    font-weight: 600;
`;

const ArrivalsCellElement = styled(ChartDataCellElement)`
    color: ${COLOR_ARRIVALS};
`;

const DeparturesCellElement = styled(ChartDataCellElement)`
    color: ${COLOR_DEPARTURES};
`;

const ContentElement = styled.div`
    position: relative;
`;
